import {
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import OrganizerDatePicker, {
  parseDateInputValue,
} from "../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput from "../../../shared/components/OrganizerTimeInput";
import type { ChallengeEventDraft } from "../../challenges/types/challengeDraft";
import {
  formatAvailabilityPeriodLines,
  parseAvailabilityPeriodLines,
} from "../tournamentAvailabilitySettings";

type TournamentAdvancedSettingsFieldsProps = {
  challengeEventDraft: ChallengeEventDraft;
  setChallengeEventDraft: Dispatch<SetStateAction<ChallengeEventDraft>>;
  onConfiguredPeriodsChange?: () => void;
  periodChangeSaving?: boolean;
};

const PERIOD_PRESETS = [
  {
    id: "full_day",
    label: "Dia todo",
    suffix: "o dia todo",
    startTime: "00:00",
    endTime: "23:59",
    type: "full_day",
  },
  {
    id: "morning",
    label: "Manhã",
    suffix: "pela manhã",
    startTime: "08:00",
    endTime: "12:00",
    type: "day_period",
  },
  {
    id: "afternoon",
    label: "Tarde",
    suffix: "à tarde",
    startTime: "12:00",
    endTime: "18:00",
    type: "day_period",
  },
  {
    id: "night",
    label: "Noite",
    suffix: "à noite",
    startTime: "18:00",
    endTime: "23:59",
    type: "day_period",
  },
  {
    id: "custom",
    label: "Intervalo específico",
    suffix: "",
    startTime: "08:00",
    endTime: "10:00",
    type: "time_range",
  },
];

function buildDateTimeIso(dateValue: string, timeValue: string) {
  const dateMatch = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return "";
  const timeMatch = String(timeValue || "23:59").match(/^(\d{1,2}):(\d{2})$/);
  const hours = timeMatch ? Number(timeMatch[1]) : 23;
  const minutes = timeMatch ? Number(timeMatch[2]) : 59;
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return "";
  }
  const [, year, month, day] = dateMatch;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    hours,
    minutes,
    0,
    0,
  ).toISOString();
}

function formatWeekday(dateValue: string) {
  const date = parseDateInputValue(dateValue);
  if (!date) return "";
  return new Intl.DateTimeFormat("pt-BR", { weekday: "long" }).format(date);
}

function getPeriodPreset(presetId: string) {
  return (
    PERIOD_PRESETS.find((item) => item.id === presetId) || PERIOD_PRESETS[0]
  );
}

function buildPeriodLabel({
  dateValue,
  presetId,
  startTime,
  endTime,
}: {
  dateValue: string;
  presetId: string;
  startTime: string;
  endTime: string;
}) {
  const weekday = formatWeekday(dateValue);
  const preset = getPeriodPreset(presetId);
  if (!weekday) return "";
  if (preset.id === "custom") return `${weekday} das ${startTime} às ${endTime}`;
  return `${weekday} ${preset.suffix}`;
}

export default function TournamentAdvancedSettingsFields({
  challengeEventDraft,
  onConfiguredPeriodsChange,
  periodChangeSaving = false,
  setChallengeEventDraft,
}: TournamentAdvancedSettingsFieldsProps) {
  const [periodDate, setPeriodDate] = useState("");
  const [periodPresetId, setPeriodPresetId] = useState("full_day");
  const [periodStartTime, setPeriodStartTime] = useState("08:00");
  const [periodEndTime, setPeriodEndTime] = useState("10:00");
  const enabled = Boolean(challengeEventDraft.availabilityRestrictionsEnabled);
  const configuredPeriods = useMemo(
    () =>
      parseAvailabilityPeriodLines(
        challengeEventDraft.availabilityRestrictionPeriodsText || "",
      ),
    [challengeEventDraft.availabilityRestrictionPeriodsText],
  );
  const selectedPeriodPreset = getPeriodPreset(periodPresetId);
  const addConfiguredPeriod = () => {
    const preset = getPeriodPreset(periodPresetId);
    const startTime =
      preset.id === "custom" ? periodStartTime : preset.startTime;
    const endTime = preset.id === "custom" ? periodEndTime : preset.endTime;
    const startAt = buildDateTimeIso(periodDate, startTime);
    const endAt = buildDateTimeIso(periodDate, endTime);
    if (
      !startAt ||
      !endAt ||
      new Date(endAt).getTime() <= new Date(startAt).getTime()
    ) {
      return;
    }
    const label = buildPeriodLabel({
      dateValue: periodDate,
      presetId: periodPresetId,
      startTime,
      endTime,
    });
    if (!label) return;
    const next = [
      ...configuredPeriods.filter(
        (period) => !(period.startAt === startAt && period.endAt === endAt),
      ),
      { label, startAt, endAt, type: preset.type },
    ];
    setChallengeEventDraft((prev) => ({
      ...prev,
      availabilityRestrictionPeriodsText: formatAvailabilityPeriodLines(next),
    }));
    onConfiguredPeriodsChange?.();
  };
  const removeConfiguredPeriod = (index: number) => {
    const next = configuredPeriods.filter((_, itemIndex) => itemIndex !== index);
    setChallengeEventDraft((prev) => ({
      ...prev,
      availabilityRestrictionPeriodsText: formatAvailabilityPeriodLines(next),
    }));
    onConfiguredPeriodsChange?.();
  };

  return (
    <div className="form-grid admin-edit-form-grid is-tournament">
      <label className="field">
        <span>Permitir restrições de disponibilidade dos participantes</span>
        <select
          value={enabled ? "yes" : "no"}
          onChange={(event) => {
            const nextEnabled = event.target.value === "yes";
            setChallengeEventDraft((prev) => ({
              ...prev,
              availabilityRestrictionsEnabled: nextEnabled,
              availabilityRestrictionLimit:
                nextEnabled && !prev.availabilityRestrictionLimit
                  ? "1"
                  : prev.availabilityRestrictionLimit,
            }));
          }}
        >
          <option value="no">Não</option>
          <option value="yes">Sim</option>
        </select>
      </label>

      {enabled && (
        <>
          <div className="admin-row-two">
            <label className="field">
              <span>Valor adicional por atleta</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={challengeEventDraft.availabilityRestrictionAmount}
                onChange={(event) =>
                  setChallengeEventDraft((prev) => ({
                    ...prev,
                    availabilityRestrictionAmount: event.target.value,
                  }))
                }
                placeholder="0,00"
              />
            </label>
            <label className="field">
              <span>Quantidade máxima de restrições permitidas</span>
              <input
                type="number"
                min="1"
                value={challengeEventDraft.availabilityRestrictionLimit}
                onChange={(event) =>
                  setChallengeEventDraft((prev) => ({
                    ...prev,
                    availabilityRestrictionLimit: event.target.value,
                  }))
                }
              />
            </label>
          </div>

          <fieldset className="admin-tournament-settings-box admin-tournament-availability-periods">
            <legend>Períodos pré-configurados</legend>
            <div className="admin-row-two">
              <OrganizerDatePicker
                label="Dia da restrição"
                value={periodDate}
                onChange={setPeriodDate}
              />
              <label className="field">
                <span>Período do dia</span>
                <select
                  value={periodPresetId}
                  onChange={(event) => {
                    const nextPreset = getPeriodPreset(event.target.value);
                    setPeriodPresetId(nextPreset.id);
                    setPeriodStartTime(nextPreset.startTime);
                    setPeriodEndTime(nextPreset.endTime);
                  }}
                >
                  {PERIOD_PRESETS.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {selectedPeriodPreset.id === "custom" && (
              <div className="admin-row-two">
                <OrganizerTimeInput
                  label="Início do intervalo"
                  value={periodStartTime}
                  onChange={setPeriodStartTime}
                />
                <OrganizerTimeInput
                  label="Fim do intervalo"
                  value={periodEndTime}
                  onChange={setPeriodEndTime}
                />
              </div>
            )}

            <div className="admin-active-category-actions">
              <button
                type="button"
                className="admin-registration-filter-pill admin-availability-period-add-button"
                onClick={addConfiguredPeriod}
                disabled={!periodDate || periodChangeSaving}
              >
                {periodChangeSaving ? "Adicionando..." : "Adicionar período"}
              </button>
            </div>

            {configuredPeriods.length > 0 ? (
              <div className="admin-tournament-availability-period-list">
                {configuredPeriods.map((period, index) => (
                  <div
                    key={`${period.label}-${period.startAt}-${period.endAt}`}
                    className="admin-tournament-availability-period-item"
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      event_busy
                    </span>
                    <div>
                      <strong>{period.label}</strong>
                      <small>
                        {new Date(period.startAt).toLocaleString("pt-BR", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}{" "}
                        até{" "}
                        {new Date(period.endAt).toLocaleString("pt-BR", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="admin-registration-filter-pill admin-availability-period-remove-button"
                      onClick={() => removeConfiguredPeriod(index)}
                      disabled={periodChangeSaving}
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="admin-tournament-availability-empty">
                Cadastre os períodos que o atleta poderá escolher na inscrição,
                como sexta-feira o dia todo ou sábado pela manhã.
              </p>
            )}
          </fieldset>
        </>
      )}
    </div>
  );
}
